import { useCallback, useEffect, useRef, useState, type DragEvent as ReactDragEvent } from "react";
import { dragCarriesFiles, droppedFiles } from "@/features/intelligence/lib/attachments";

type DropHandlers = Partial<{
  onDragEnter: (e: ReactDragEvent<HTMLDivElement>) => void;
  onDragOver: (e: ReactDragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: ReactDragEvent<HTMLDivElement>) => void;
  onDrop: (e: ReactDragEvent<HTMLDivElement>) => void;
}>;

export interface FileDropTarget {
  dropping: boolean;
  dropTarget: DropHandlers;
}

export function useFileDropTarget(onFile: ((file: File) => void) | undefined): FileDropTarget {
  const [dropping, setDropping] = useState(false);
  const dragDepth = useRef(0);
  const clearDrop = useCallback(() => {
    dragDepth.current = 0;
    setDropping(false);
  }, []);

  useEffect(() => {
    const swallow = (e: DragEvent) => {
      if (!dragCarriesFiles(e.dataTransfer)) return;
      e.preventDefault();
    };
    const ended = (e: DragEvent) => {
      if (!dragCarriesFiles(e.dataTransfer)) return;
      clearDrop();
    };
    window.addEventListener("dragover", swallow, true);
    window.addEventListener("drop", swallow, true);
    window.addEventListener("drop", ended, true);
    window.addEventListener("dragend", ended, true);
    return () => {
      window.removeEventListener("dragover", swallow, true);
      window.removeEventListener("drop", swallow, true);
      window.removeEventListener("drop", ended, true);
      window.removeEventListener("dragend", ended, true);
    };
  }, [clearDrop]);

  if (!onFile) return { dropping: false, dropTarget: {} };

  return {
    dropping,
    dropTarget: {
      onDragEnter: (e) => {
        if (!dragCarriesFiles(e.dataTransfer)) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDropping(true);
      },
      onDragOver: (e) => {
        if (!dragCarriesFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        if (!dropping) setDropping(true);
      },
      onDragLeave: (e) => {
        if (!dragCarriesFiles(e.dataTransfer)) return;
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) clearDrop();
      },
      onDrop: (e) => {
        clearDrop();
        if (!dragCarriesFiles(e.dataTransfer)) return;
        e.preventDefault();
        for (const file of droppedFiles(e.dataTransfer)) onFile(file);
      },
    },
  };
}
